/**
 * Modelo: Estado de Factura
 *
 * Enum con los estados exactos del backend.
 * Legacy: ConstantsEJB.java
 */
export enum EstadoFactura {
  INICIAL = 'PEN',
  CALCULADA = 'CALC',
  APROBADA = 'APR',
  ENVIADA_SAP = 'ENVS',
  ENVIADA_CLIENTE = 'ENVC',
  ANULADA = 'ANU',
  ELIMINADA = 'DEL'
}

/**
 * Descripciones de estados para mostrar en UI
 */
export const ESTADOS_FACTURA_LABELS: Record<EstadoFactura, string> = {
  [EstadoFactura.INICIAL]: 'Pendiente',
  [EstadoFactura.CALCULADA]: 'Calculada',
  [EstadoFactura.APROBADA]: 'Aprobada',
  [EstadoFactura.ENVIADA_SAP]: 'Enviada SAP',
  [EstadoFactura.ENVIADA_CLIENTE]: 'Enviada Cliente',
  [EstadoFactura.ANULADA]: 'Anulada',
  [EstadoFactura.ELIMINADA]: 'Eliminada'
};

/**
 * Colores para badges de estados en PrimeNG
 */
export const ESTADOS_FACTURA_SEVERITIES: Record<
  EstadoFactura,
  'info' | 'warning' | 'success' | 'danger'
> = {
  [EstadoFactura.INICIAL]: 'info',
  [EstadoFactura.CALCULADA]: 'warning',
  [EstadoFactura.APROBADA]: 'success',
  [EstadoFactura.ENVIADA_SAP]: 'success',
  [EstadoFactura.ENVIADA_CLIENTE]: 'success',
  [EstadoFactura.ANULADA]: 'danger',
  [EstadoFactura.ELIMINADA]: 'danger'
};

/**
 * Modelo: Encabezado de Factura
 *
 * Replica el DTO del backend: FacturaEncabezadoResponse.java
 * Tabla legacy: TPSC_FACTURA_ENCABEZADO
 */
export interface FacturaEncabezado {
  nmconsecutivo: number;
  cdfactura: string;
  fefactura: string; // Fecha ISO format
  dsperiodo: string; // YYYYMM
  feinicial?: string;
  fefinal?: string;
  cdservicioFacturacion: number; // 1=PDF, 2=Otros, 3=Empresariales
  servicioFacturadoDescripcion: string;
  tipoFacturacionDescripcion?: string;
  condicionPagoDias?: number;
  condicionPagoPorcentaje?: number;
  nmconsecutivoConvenio: number;
  convenioDescripcion: string;
  nmconsecutivoOficina: number;
  oficinaDescripcion: string;
  cdestado: EstadoFactura;
  estadoDescripcion: string;
  pttotal: number;
  nmdocumentoSap?: string;
  feenvioSap?: string;
  feenvioCliente?: string;
  clienteNombre?: string;
  dsobservaciones?: string;
}

export interface ActualizarEncabezadoFacturaPayload {
  cdfactura: string;
  dsobservaciones?: string;
}

/**
 * Payload de creación de encabezado de factura.
 *
 * Legacy: encabezado-factura.ts
 */
export interface EncabezadoFactura {
  nmconsecutivo?: number;
  cdfactura: string;
  nmconsecutivoOficina: number;
  nmconsecutivoConvenio: number;
  cdservicioFacturacion: number;
  anio: number;
  mes: number;
  dsobservaciones?: string;
  dsperiodo?: string;
  cdestado?: string;
  feinicial?: string;
  fefinal?: string;
}

/**
 * Periodo de facturación devuelto por backend.
 *
 * Legacy: periodo-facturacion.ts
 */
export interface PeriodoFacturacion {
  nmconsecutivo?: number;
  nmmes: number;
  nmano: number;
  nmconsecutivoOficina: number;
  fedesde: string;
  fehasta: string;
}

/**
 * Resultado de validación de periodo para nueva factura.
 *
 * Legacy: validacion-factura.ts
 */
export interface ValidacionFactura {
  valido: boolean;
  mensaje?: string;
  periodo?: PeriodoFacturacion;
}

/**
 * Filtros para búsqueda de facturas
 */
export interface FiltroFacturas {
  cdfactura?: string;
  mes?: number;
  anio?: number;
  nmconsecutivoCliente?: number;
  numeroIdentificacionCliente?: string;
  nombreCliente?: string;
  nmconsecutivoConvenio?: number;
  nombreConvenio?: string;
  nmconsecutivoOficina?: number;
  nombreOficina?: string;
  incluirAnuladas?: boolean;
}

/**
 * Resultado paginado de facturas
 */
export interface ResultadoPaginadoFacturas {
  facturas: FacturaEncabezado[];
  totalRegistros: number;
  paginaActual: number;
  tamanioPagina: number;
  totalPaginas: number;
  tienePaginaSiguiente: boolean;
  tienePaginaAnterior: boolean;
}

/**
 * Respuesta de operaciones mutables sobre factura.
 */
export interface OperacionFacturaResponse {
  mensaje: string;
}

/**
 * Respuesta del endpoint "agregar/modificar detalle" de una factura.
 *
 * Legacy: {@code FacturaEncabezadoFacade.agregarDetalleFacEvento} devuelve
 * `GenericoRespuesta { exito, mensaje }`.
 */
export interface AgregarDetalleFacturaResponse {
  exito: boolean;
  mensaje: string;
}

export interface FiltroDetalleFactura {
  nmconsecutivoCumplimiento?: number;
  feprogramacion?: string;
  cdmunicipioEntrega?: string;
  nmconsecutivoAsesor?: number;
  nmconsecutivoBeneficiario?: number;
  snpagoTransporteEmpleado?: string;
}

export interface FacturaDetallePrefactura {
  nmconsecutivo: number;
  nmconsecutivoCumplimiento?: number;
  nmconsecutivoTipoCita?: number;
  nmconsecutivoAsesor?: number;
  nmconsecutivoBeneficiario?: number;
  dsasesor?: string;
  dsbeneficiario?: string;
  dsservicio?: string;
  dsclasificacion?: string;
  dstipo?: string;
  sntipoInforme?: string;
  snatencionVirtual?: string;
  feprogramacion?: string;
  nmcantidadEjecutada?: number;
  cdmunicipioOrigen?: string;
  cdmunicipioEntrega?: string;
  dsmunicipioOrigen?: string;
  dsmunicipioEntrega?: string;
  ptvalorTransporte?: number;
  ptvalorTransporteAdicional?: number;
  ptvalorAdicionalCons?: number;
  ptvalorViaticos?: number;
  snpagoTransporteEmpleado?: string;
  snpagoViaticos?: string;
  nmconsecutivoTransporteProveedor?: number;
  dstipoTransporte?: string;
  dsproveedorTransporte?: string;
  dsespecialTransporte?: string;
  dsvaletaxiTransporte?: string;
  dsobservacionViaticos?: string;
}

export interface ResultadoPaginadoDetalleFactura {
  detalles: FacturaDetallePrefactura[];
  totalRegistros: number;
  tamanioPagina: number;
  nextCursor?: string;
  previousCursor?: string;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface GuardarDetalleFacturaPayload {
  detalles: Array<{
    nmconsecutivo: number;
    nmconsecutivoCumplimiento?: number;
    nmcantidadEjecutada?: number;
    cdmunicipioOrigen?: string;
    cdmunicipioEntrega?: string;
    ptvalorTransporte?: number;
    ptvalorTransporteAdicional?: number;
    ptvalorAdicionalCons?: number;
    ptvalorViaticos?: number;
    snpagoViaticos?: string;
    nmconsecutivoTransporteProveedor?: number;
    dsproveedorTransporte?: string;
    dsespecialTransporte?: string;
    dsvaletaxiTransporte?: string;
    dsobservacionViaticos?: string;
  }>;
}

export interface TransporteProveedor {
  nmconsecutivo: number;
  dsnombre: string;
}

/**
 * Opciones para el dropdown de mes
 */
export const MESES_OPTIONS = [
  { label: 'Enero', value: 1 },
  { label: 'Febrero', value: 2 },
  { label: 'Marzo', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Mayo', value: 5 },
  { label: 'Junio', value: 6 },
  { label: 'Julio', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Septiembre', value: 9 },
  { label: 'Octubre', value: 10 },
  { label: 'Noviembre', value: 11 },
  { label: 'Diciembre', value: 12 }
];

/**
 * Opciones para tamaños de página
 */
export const PAGE_SIZE_OPTIONS_FACTURAS = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 }
];
